/**
 * A hook that returns whether the category (either passed in or from context) is active.
 *
 * A category is active when it is the category in the url param for the current page or an article inside that category is in the url param.
 *
 * Utilises [[useCategory]] hook under the hood.
 *
 * Accepts an optional categoryId as the parameter.
 *
 * - If no category is found from `useCategory`, returns `false`.
 * - If a `categoryId` is provided, it checks to see if it matches with the `category` currently in `useCategory`.
 * - Returns `false` if no `category`, `categoryId` or `fragment` are found.
 *
 *  @module
 */
import { useContext } from 'react';
import { useSelector } from '../actions/types';
import * as selectors from '../selectors';
import { CategoryContext } from '../contexts';
import { usePageInfo } from './usePageInfo';
/**
 * # useIsCategoryActive
 *
 * Usage example:
 * ```javascript
 * import useIsCategoryActive from '@elevio/kb-kit/lib/hooks';
 *
 * const ActiveCategoryChecker = ({
 *   categoryId,
 *   exactMatch,
 * }: {
 *   categoryId?: string;
 *   exactMatch: boolean;
 * }) => {
 *   const isActive = useIsCategoryActive({ categoryId, exactMatch });
 *   console.log(isActive); // boolean
 *
 *   return <div>{`Category is ${isActive ? 'active ✅' : 'inactive ❌'}`}</div>;
 * };
 * ```
 */
export function useIsCategoryActive({ categoryId, exactMatch, }) {
    const pageInfo = usePageInfo();
    const categoryFromContext = useContext(CategoryContext);
    const pageCategory = useSelector((state) => {
        if (!((pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.pageType) === 'category'))
            return null;
        return selectors.categoryTree.category(state, pageInfo.categoryId);
    });
    const pageArticle = useSelector((state) => {
        if (!((pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.pageType) === 'article'))
            return null;
        return selectors.categoryTree.articleFragment(state, pageInfo.articleId);
    });
    const pageArticleCategory = useSelector((state) => {
        if (!pageArticle)
            return null;
        return selectors.categoryTree.category(state, pageArticle.categoryId);
    });
    // Favor the passed in category id
    const catId = categoryId || categoryFromContext;
    // Nothing to check against.
    if (!catId)
        return false;
    if (exactMatch) {
        // Not a category page
        if (!pageInfo || pageInfo.pageType !== 'category')
            return false;
        return catId === pageInfo.categoryId;
    }
    if (pageCategory) {
        if (catId === pageCategory.id)
            return true;
        return pageCategory.parentCategory === catId;
    }
    if (pageArticle) {
        if (!pageArticleCategory)
            return false;
        if (catId === pageArticleCategory.id)
            return true;
        if (catId === pageArticleCategory.parentCategory)
            return true;
    }
    return false;
}
//# sourceMappingURL=useIsCategoryActive.js.map